import ContactSelect from '@/components/Form/Shared/ContactSelect'
import CustomerSelect from '@/components/Form/Shared/CustomerSelect'
import SubHeaders from '@/components/Headers/SubHeaders'
import { useFormPersistence } from '@/hooks/useFormPersistence'
import AddIcons from '@/icons/AddIcons'
import { formatPGDate, omit } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { useMemo, useEffect } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Alert, GridHookForm, Typogaphy } from 'vnetwork-ui'
import { createServicePOC, getCustomerJourneyById } from '../../services'
import { ServicePOCProps } from '../../types'
import servicePocSchema from './schema'
import PocPaidFields from './poc-paid-fields'
import ProductTemplate from '@/modules/Contract/components/New/ProductTemplate'
import { initServiceTimes } from '@/modules/Contract/constants/function'
import toast from 'react-hot-toast'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const today = dayjs()
const POC_UNAVAILABLE_SERVICE_GROUPS = ['OTHER', 'KHÁC', 'KHAC']
// Same currencies as the contract form (CustomConfigSection)
const CURRENCY_OPTIONS = [
    { label: 'VND', value: 'VND' },
    { label: 'USD', value: 'USD' },
    { label: 'EUR', value: 'EUR' },
    { label: 'JPY', value: 'JPY' },
]

const POC = () => {
    const { id } = useParams()
    const { t } = useTranslation()
    const [search] = useSearchParams()

    const customerJourneyId = Number(search.get('customer_journey_id'))
    // The journey links pass the contact; without it POCs were saved with contact_id 0
    const contactId = Number(search.get('contact_id')) || 0

    const defaultValues = useMemo(
        () => ({
            customer_id: Number(id || 0),
            service_start_date: today,
            service_stopped_date: today.add(6, 'days'),
            contact_id: contactId,
            customer_journey_id: customerJourneyId,
            currency_code: 'VND',
            exchange_rate: 1,
            order: {
                products: [
                    {
                        product_id: 0,
                        product_group_name: '',
                        product_form: 'commit',
                        editable: true,
                        service_times: initServiceTimes(today),
                        prepaid: true,
                        service_configoptions: [],
                        is_paid: false,
                        poc_price: 0,
                    },
                ],
            },
        }),
        [id, customerJourneyId, contactId]
    )
    const navigation = useNavigate()

    // Fetch customer journey if customer_journey_id exists
    const { data: customerJourney } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.CUSTOMER_JOURNEY, id, customerJourneyId],
            queryFn: () =>
                getCustomerJourneyById({
                    customer_id: Number(id),
                    customer_journey_id: customerJourneyId,
                }),
            enabled: !!id && !!customerJourneyId && customerJourneyId > 0,
        },
        {
            showLoading: false,
            showError: false,
        }
    )

    const formControl = useForm<ServicePOCProps>({
        defaultValues,
        resolver: yupResolver(servicePocSchema as any),
    })
    useFormPersistence(formControl, `poc-draft-${id}`, defaultValues)

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: createServicePOC },
        {
            reactQueryKeys: [QUERY_KEYS.CUSTOMERS, QUERY_KEYS.SERVICES],
            successMessage: 'create-service-poc-success',
            errorMessage: 'something went wrong',
            onSuccess: (data) => {
                localStorage.removeItem(`poc-draft-${id}`)
                if (data?.services?.length === 1) {
                    navigation(
                        generatePathDatacentral(paths.serviceDetail, {
                            id: data.services[0].service_id,
                        })
                    )
                } else {
                    navigation(
                        generatePathDatacentral(paths.customerDetail, {
                            id: id,
                        }) + '?tab=service'
                    )
                }
            },
        }
    )
    const { append, fields, remove } = useFieldArray({
        name: 'order.products',
        control: formControl.control,
    })

    // Auto fill services from customer journey
    useEffect(() => {
        if (
            customerJourney?.result?.customer_journey_explaination
                ?.service_interestings &&
            customerJourney?.result?.customer_journey_explaination
                ?.service_interestings?.length > 0
        ) {
            const serviceInterestings =
                customerJourney.result.customer_journey_explaination
                    .service_interestings

            // Clear existing products
            formControl.setValue('order.products', [])

            // Add products based on service interestings
            serviceInterestings
                .filter(
                    (service) =>
                        !POC_UNAVAILABLE_SERVICE_GROUPS.includes(
                            service?.toUpperCase?.() || service
                        )
                )
                .forEach((service) => {
                    append({
                        product_id: 0,
                        product_group_name: service,
                        product_form: 'commit',
                        editable: true,
                        service_configoptions: [],
                        is_paid: false,
                        poc_price: 0,
                    })
                })
        }
    }, [customerJourney, append, formControl])

    const handleAddProduct = () => {
        append({
            product_id: 0,
            product_group_name: '',
            product_form: 'commit',
            editable: true,
            service_times: initServiceTimes(today),
            prepaid: true,
            service_configoptions: [],
            is_paid: false,
            poc_price: 0,
        })
    }

    const onSubmit = async (values: ServicePOCProps) => {
        if (values?.order?.products?.length === 0) {
            toast.error(t('please-select-service'))
            return
        }
        mutateAsync({
            ...omit(values, ['order']),
            // Drafts saved before these fields existed restore without them
            currency_code: values?.currency_code || 'VND',
            exchange_rate: values?.exchange_rate ?? 1,
            products: values?.order?.products?.map((product) => ({
                ...product,
                is_paid: !!product?.is_paid,
                poc_price: product?.is_paid ? Number(product?.poc_price) : 0,
            })),
            service_start_date: formatPGDate(values?.service_start_date || ''),
            service_stopped_date: formatPGDate(
                values?.service_stopped_date || ''
            ),
        })
    }
    return (
        <>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.customersList,
                        title: t('customer_manager'),
                    },
                    {
                        path: generatePathDatacentral(paths.customerDetail, {
                            id,
                        }),
                        title: `${id}`,
                    },
                    {
                        path: '',
                        title: t('create-service-poc'),
                    },
                ]}
            />
            <FormProvider {...formControl}>
                <div className="bg-white p-4 rounded-lg mt-2">
                    <Typogaphy size="medium" className="font-medium">
                        {t('customers')}
                    </Typogaphy>
                    <GridHookForm
                        className="mt-4"
                        formControl={formControl}
                        actions={[
                            {
                                title: t('select-service'),
                                type: 'button',
                                variant: 'outlined',
                                startIcon: <AddIcons />,
                                disabled: isPending,
                                handler: handleAddProduct,
                            },
                            {
                                title: t('save'),
                                type: 'submit',
                                variant: 'contained',
                                loading: isPending,
                                disabled: isPending,
                            },
                        ]}
                        data={[
                            {
                                renderEditComponent: (control) => {
                                    return (
                                        <CustomerSelect
                                            disabled
                                            control={control}
                                            name="customer_id"
                                            showLabel
                                        />
                                    )
                                },
                                className: 'md:col-span-2 col-span-2',
                            },
                            {
                                renderEditComponent: (control) => {
                                    return (
                                        <ContactSelect
                                            t={t}
                                            control={control}
                                            customer_id={Number(id)}
                                            name="contact_id"
                                            placeholder={t(
                                                'contact_placeholder'
                                            )}
                                        />
                                    )
                                },
                                className: 'md:col-span-2 col-span-2',
                            },
                            {
                                formItemProps: {
                                    name: 'service_start_date',
                                    label: t('service-poc-start-date'),
                                    type: 'datepicker',
                                    placeholder: t('service-poc-start-date'),
                                },
                                className: 'col-span-2',
                            },
                            {
                                formItemProps: {
                                    name: 'service_stopped_date',
                                    label: t('service-poc-end-date'),
                                    type: 'datepicker',
                                    placeholder: t('service-poc-end-date'),
                                },
                                className: 'col-span-2',
                            },
                            {
                                formItemProps: {
                                    name: 'currency_code',
                                    type: 'select',
                                    label: t('contract:currency_code'),
                                    options: CURRENCY_OPTIONS,
                                    allowClear: false,
                                },
                                className: 'col-span-2',
                            },
                            {
                                formItemProps: {
                                    name: 'exchange_rate',
                                    type: 'number',
                                    label: t('contract:exchange_rate'),
                                },
                                className: 'col-span-2',
                            },
                            {
                                className: 'col-span-4',
                                renderEditComponent: () => {
                                    return (
                                        <Alert variant="warning" className="">
                                            {t('poc-auto-ticket-notice')}
                                        </Alert>
                                    )
                                },
                            },
                            {
                                renderEditComponent: () => {
                                    return (
                                        <div className="space-y-4">
                                            {fields?.map((item, index) => {
                                                return (
                                                    <div
                                                        key={item?.id}
                                                        className="space-y-2"
                                                    >
                                                        <ProductTemplate
                                                            isPOC={true}
                                                            index={index}
                                                            remove={remove}
                                                        />
                                                        <PocPaidFields
                                                            index={index}
                                                        />
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )
                                },
                                className:
                                    'xl:col-span-4 lg:col-span-3 md:col-span-2 col-span-2',
                            },
                        ]}
                        onSubmit={onSubmit}
                    />
                </div>
            </FormProvider>
        </>
    )
}

export default POC
